import { useEffect, useState, useRef } from "react";

import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import InputAdornment from "@mui/material/InputAdornment";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import ipTrendAllowanceService from "@plannel/services/ip/ip-trend-allowance-service";
import locationService from "@plannel/services/data/location-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import { IPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import Dialog from "@plannel/components/Dialog"
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import { withTranslation } from "react-i18next";
import isNull from "lodash/isNull";

const ManageTrendAllowanceModal = ({ t, evalMonths, modalOpen, setModalOpen, setSnackInfo, retrieve }) => {
  const INIT_DEMAND_TREND = { allownceRatioUptrend: "", allownceRatioDntrend: "" };
  const INIT_INV_RISK = { allownceRatioStockout: "", allownceRatioExcess: "", allownceRatioObsolesce: "" };
  const INIT_FILTER_CONDITION = { location: null, category: null };
  const INIT_ALLOWANCE_RATIO = 50;

  const [filterCond, setFilterCond] = useState(INIT_FILTER_CONDITION);
  const itemSearch = useRef("");

  const [candidates, setCandidates] = useState(0);
  const [demandTrend, setDemandTrend] = useState(INIT_DEMAND_TREND);
  const [invRisk, setInvRisk] = useState(INIT_INV_RISK);

  const [locations, setLocations] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);

  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [backDropOpen, setBackDropOpen] = useState(false);
  const { snackBarMessageColor } = ColorUtil;

  const onChangeDemandTrend = (e) => {
    const { name, value } = e.target;

    setDemandTrend({ ...demandTrend, [name]: isNaN(value) ? "" : value });
  }

  const onChangeInvRisk = (e) => {
    const { name, value } = e.target;

    setInvRisk({ ...invRisk, [name]: isNaN(value) ? "" : value });
  }

  const isStringEmpty = value => {
    return value === "";
  }

  const setDecimal = value => {
    return Number(value.toFixed(2));
  }

  const changePercent = rawObj => {
    const obj = {};
    Object.keys(rawObj).forEach(key => {
      if (isStringEmpty(rawObj[key])) {
        obj[key] = INIT_ALLOWANCE_RATIO / 100;
      } else {
        obj[key] = setDecimal(rawObj[key] / 100);
      }
    });

    return obj;
  }

  const getModalRequestParams = (includedAllowance = false) => {
    const params = {};
    params.nowDate = new ZDate();

    if (filterCond?.location) {
      params.locCd = filterCond.location.locCd;
    }

    if (filterCond?.category) {
      params.categoryCd = filterCond.category.hrchyCd;
    }

    if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (includedAllowance) {
      return { ...params, ...changePercent(demandTrend), ...changePercent(invRisk) };
    }

    return params;
  }

  const setDefaultAllowance = trendAllowance => {
    const allownceRatioUptrend = setDecimal(trendAllowance.allownceRatioUptrend * 100);
    const allownceRatioDntrend = setDecimal(trendAllowance.allownceRatioDntrend * 100);
    const allownceRatioStockout = setDecimal(trendAllowance.allownceRatioStockout * 100);
    const allownceRatioExcess = setDecimal(trendAllowance.allownceRatioExcess * 100);
    const allownceRatioObsolesce = setDecimal(trendAllowance.allownceRatioObsolesce * 100);

    const curDemandTrend = { allownceRatioUptrend, allownceRatioDntrend };
    const curInvRisk = { allownceRatioStockout, allownceRatioExcess, allownceRatioObsolesce };

    setDemandTrend(curDemandTrend);
    setInvRisk(curInvRisk);
  }

  const getTrendAllowance = () => {
    const params = getModalRequestParams();
    ipTrendAllowanceService.getTrendAllowance(params)
      .then(response => {
        const { count, defaultTrendAllowance } = response.data;
        setCandidates(count);
        if (isNull(defaultTrendAllowance)) {
          setDemandTrend(INIT_DEMAND_TREND);
          setInvRisk(INIT_INV_RISK);
        } else {
          setDefaultAllowance(defaultTrendAllowance);
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveLocation = () => {
    const params = { pagination: false };
    locationService.getAll(params)
      .then(response => {
        setLocations(response.data.results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveItemCategory = () => {
    const params = { hrchyNum : 1}
    itemHrchyService.getLookup(params)
      .then(response => {
        setItemCategories(response.data.results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const isInvalid = value => {
    return value < 0;
  }

  const isInvalidValues = (obj) => {
    const values = Object.values(obj);
    for (const value of values) {
      if (isInvalid(value)) {
        return true;
      }
    }
    return false;
  }

  const updateTrendAllowance = () => {
    setBackDropOpen(true);
    const params = getModalRequestParams(true);
    ipTrendAllowanceService.upsertTrendAllowance(params)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        setModalOpen(false);
        retrieve();
      })
      .catch(e => {
        const detailErrorMsgInfo = e.response?.data?.message?.message ? { content: t(e.response?.data?.message?.message), duration: 3000 } : {};
        debug(e);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed"), ...detailErrorMsgInfo });
      })
      .then(() => {
        setBackDropOpen(false);
      });
  }

  const handleDialog = (action) => {
    if (action === "UPDATE") {
      updateTrendAllowance();
    }
  };

  const handleClose = (action) => {
    if (action === "confirm") {
      if (candidates === 0) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoDataToUpdate") });
      } else if (isInvalidValues(demandTrend) || isInvalidValues(invRisk)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_TrendRiskValueRequired") });
      } else {
        setDialogInfo({ open: true, title: "update", content: t("MSG_SaveConfirm") });
      }
    } else if (action === "cancel") {
      setModalOpen(false);
    }
  }

  const emptyValueText = (value) => {
    if (isStringEmpty(value)) {
      return ` ${INIT_ALLOWANCE_RATIO}%`;
    }
    return ` ${value}%`;
  }

  const percentEndAdornment = value => {
    if (value) {
      return { endAdornment: <InputAdornment position="start">%</InputAdornment> }
    }

    return {};
  }

  useEffect(() => {
    if (modalOpen) {
      retrieveLocation();
      retrieveItemCategory();
      getTrendAllowance();
    } else {
      setFilterCond(INIT_FILTER_CONDITION);
    }

    return () => {
      setCandidates(0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, filterCond.location, filterCond.category]);

  return (
    <>
      {/* handleClose("confirm") 은 확인 Dialog 를 거쳐 updateTrendAllowance() 를 호출한다 —
          실제로 저장하므로 confirm 이 아니라 save 다.
          width 780: 셸 프리셋(최대 lg=500)보다 넓은 예외 케이스라 숫자를 직접 준다. */}
      <BaseConfigModal
        open={modalOpen}
        onClose={() => handleClose("cancel")}
        title={t("mgmtTrendAllowance")}
        width={780}
        onSave={() => handleClose("confirm")}>
          <Box>
            <Box sx={{ my: 0.5 }}>
              <Typography id="modal-modal-title" variant="subtitle1">{t("filterCond")}</Typography>
            </Box>
            <Grid container direction="column" justifyContent="center">
              <Grid item>
                <Grid container>
                  <Grid item xs={4}>
                    <Box id="manage-trend-allowance-modal-loc">
                    <Autocomplete
                      options={locations}
                      getOptionLabel={option => option.name}
                      value={filterCond.location}
                      onChange={(_, newValue) => setFilterCond({ ...filterCond, location: newValue })}
                      isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
                      size="small"
                      sx={{ mr: 1.5, mt: 1, width: 230 }}
                      renderInput={(params) => (
                        <TextField {...params} label={t("location")} />
                      )}
                    />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box id="manage-trend-allowance-modal-candidate">
                      <TextField
                        id="candidates"
                        name="candidates"
                        value={candidates}
                        size="small"
                        sx={{ width: 230, mt: 1, ml: "auto" }}
                        label={t("candidates")}
                        InputProps={{ readOnly: true }}
                        error={candidates === 0}
                        variant="outlined"
                        onChange={e => setCandidates(e.target.value)} />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Box id="manage-trend-allowance-modal-item-category">
                  <Autocomplete
                    options={itemCategories}
                    getOptionLabel={option => option.name}
                    value={filterCond.category}
                    onChange={(_, newValue) => setFilterCond({ ...filterCond, category: newValue })}
                    isOptionEqualToValue={(option, value) => option.hrchyCd === value.hrchyCd}
                    size="small"
                    sx={{ mr: 1.5, mt: 1, width: 230 }}
                    renderInput={(params) => (
                      <TextField {...params} label={t("itemCategory")} />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item>
                <Box id="manage-trend-allowance-modal-item-auto">
                <IPItemAutocomplete retrieve={getTrendAllowance} searchValue={itemSearch} sx={{ width: 230, mt: 1 }} />
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ mt: 2 }} />
          <Box sx={{ px: 2 }}>
            <Box sx={{ my: 0.5 }}>
              <Typography id="modal-modal-title" variant="subtitle1">{t("demandTrend")}</Typography>
            </Box>
            <Grid container direction="column" justifyContent="center">
              <Grid item>
                <Grid container>
                  <Grid item xs={3}>
                    <TextField
                      id="allownceRatioUptrend"
                      name="allownceRatioUptrend"
                      value={demandTrend.allownceRatioUptrend}
                      size="small"
                      sx={{ width: 160, mt: 1 }}
                      label={t("rising")}
                      variant="outlined"
                      placeholder={"50"}
                      error={isInvalid(demandTrend.allownceRatioUptrend)}
                      onChange={onChangeDemandTrend}
                      InputProps={percentEndAdornment(demandTrend.allownceRatioUptrend)} />
                  </Grid>
                  <Grid item xs={8.5} sx={{ alignSelf: "center" }}>
                    <Typography variant="body2" component={"span"}>{t("MSG_SalesIncresed", { month: evalMonths, N: emptyValueText(demandTrend.allownceRatioUptrend) })}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container>
                  <Grid item xs={3}>
                    <TextField
                      id="allownceRatioDntrend"
                      name="allownceRatioDntrend"
                      value={demandTrend.allownceRatioDntrend}
                      size="small"
                      sx={{ width: 160, mt: 1 }}
                      label={t("shrinking")}
                      variant="outlined"
                      placeholder={"50"}
                      error={isInvalid(demandTrend.allownceRatioDntrend)}
                      onChange={onChangeDemandTrend}
                      InputProps={percentEndAdornment(demandTrend.allownceRatioDntrend)} />
                  </Grid>
                  <Grid item xs={8.5} sx={{ alignSelf: "center" }}>
                    <Typography variant="body2" component={"span"}>{t("MSG_SalesDecreased", { month: evalMonths, N: emptyValueText(demandTrend.allownceRatioDntrend) })}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box sx={{ my: 0.5, mt: 1 }}>
              <Typography component={'span'} variant="subtitle1">{t("inventoryRisk")}</Typography>
            </Box>
            <Grid container direction="column" justifyContent="center">
              <Grid item>
                <Grid container>
                  <Grid item xs={3}>
                    <TextField
                      id="allownceRatioStockout"
                      name="allownceRatioStockout"
                      value={invRisk.allownceRatioStockout}
                      size="small"
                      sx={{ width: 160, mt: 1 }}
                      label={t("stockout")}
                      variant="outlined"
                      placeholder={"50"}
                      error={isInvalid(invRisk.allownceRatioStockout)}
                      onChange={onChangeInvRisk}
                      InputProps={percentEndAdornment(invRisk.allownceRatioStockout)} />
                  </Grid>
                  <Grid item xs={8.5} sx={{ alignSelf: "center" }}>
                    <Typography variant="body2" component={"span"}>{t("MSG_InvBelowTargetStock", { N: emptyValueText(invRisk.allownceRatioStockout) })}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container>
                  <Grid item xs={3}>
                    <TextField
                      id="allownceRatioExcess"
                      name="allownceRatioExcess"
                      value={invRisk.allownceRatioExcess}
                      size="small"
                      sx={{ width: 160, mt: 1 }}
                      label={t("excess")}
                      variant="outlined"
                      placeholder={"50"}
                      error={isInvalid(invRisk.allownceRatioExcess)}
                      onChange={onChangeInvRisk}
                      InputProps={percentEndAdornment(invRisk.allownceRatioExcess)} />
                  </Grid>
                  <Grid item xs={8.5} sx={{ alignSelf: "center" }}>
                    <Typography variant="body2" component={"span"}>{t("MSG_InvExceedTargetStock", { N: emptyValueText(invRisk.allownceRatioExcess) })}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container>
                  <Grid item xs={3}>
                    <TextField
                      id="allownceRatioObsolesce"
                      name="allownceRatioObsolesce"
                      value={invRisk.allownceRatioObsolesce}
                      size="small"
                      sx={{ width: 160, mt: 1 }}
                      label={t("obsolescence")}
                      variant="outlined"
                      placeholder={"50"}
                      error={isInvalid(invRisk.allownceRatioObsolesce)}
                      onChange={onChangeInvRisk}
                      InputProps={percentEndAdornment(invRisk.allownceRatioObsolesce)} />
                  </Grid>
                  <Grid item xs={8.5} sx={{ alignSelf: "center", height: 48 }}>
                    <Typography variant="body2">{t("MSG_NoSalesLastMonthAnd")}</Typography>
                    <Typography variant="body2" component={"span"}>{t("MSG_InvExceedTargetStock", { N: emptyValueText(invRisk.allownceRatioObsolesce) })}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box m={2}>
            <Typography variant="body2">{`* ${t("MSG_NotEnteredDefaultSetting", { value: `${INIT_ALLOWANCE_RATIO}%` })}`}</Typography>
          </Box>
      </BaseConfigModal>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Backdrop sx={{ color: '#fff', zIndex: 1400 }} open={backDropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default withTranslation()(ManageTrendAllowanceModal);